import React, { useState, useRef, useEffect } from "react";
import { useShallow } from "zustand/react/shallow";

import { Input, Button, Radio, RadioGroup, FormControlLabel, FormControl, Select, MenuItem, TextField } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";

interface State {
  editMode: string,
  codeType: string,
  code: string,
  codeTypeCodes: Code[]
}

const defState = {
  editMode: "add",
  codeType: "",
  code: "",
  codeTypeCodes: []
};

interface Info {
  codeType: string,
  code: string,
  value: string,
  sortOrder: string,
  useYn: string
}

const defInfo = {
  codeType: "",
  code: "",
  value: "",
  sortOrder: "",
  useYn: "Y"
};

const validationMsg = [
  {id: "codeType", type: "NotBlank", msg: "대분류코드를 선택해 주세요."},
  {id: "code", type: "NotBlank", msg: "코드를 입력해 주세요."},
  {id: "code", type: "Duplicate", msg: "이미 등록된 코드입니다."},
  {id: "value", type: "NotBlank", msg: "코드명을 입력해 주세요."},
  {id: "sortOrder", type: "NotNull", msg: "정렬순서를 입력해 주세요."},
  {id: "useYn", type: "NotBlank", msg: "사용여부를 선택해 주세요."}
];

function CodeDetail(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [info, setInfo] = useState<Info>(kutil.deepCopy(defInfo));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const location = useTabsStore((state) => state.getLocation(props.id));

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfo();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    try {
      const data = await kutil.sendGet("/api/system/code_detail.data?mode=initData", {}, {
        reqFunction: init
      });
      state.codeTypeCodes = data.codeTypeCodes;
      setState({ ...state });
      return true;
    } catch(e) {
      console.log(e);
      return false;
    }
  }

  async function initParams() {
    const query = Object.fromEntries(new URLSearchParams(location.search).entries());
    state.editMode = query.codeType && query.code ? "update" : "add";
    state.codeType = query.codeType || "";
    state.code = query.code || "";
    setState({ ...state });
  }

  async function searchInfo() {
    if (!state.codeType || !state.code) {
      return;
    }

    try {
      const data = await kutil.sendGet("/api/system/code_detail.data?mode=search", {codeType: state.codeType, code: state.code}, {});
      setInfo(data.info);
    } catch(e) {
      console.log(e);
    }
  }

  async function save() {
    const isValid = await kutil.checkValidation(info, validationMsg);
    if (isValid == false) {
      return;
    }

    const confirmMsg = state.editMode == "update" ? "수정" : "등록";
    if (await kutil.confirm(confirmMsg + "하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/system/code_detail.proc?mode=" + state.editMode, info, {
        validationMsg: validationMsg
      });
      if (state.editMode == "update") {
        await kutil.alert("수정하였습니다.");
        router.reload();
      } else if (state.editMode == "add") {
        await kutil.alert("등록하였습니다.");
        goList();
      }
    } catch(e) {
      console.log(e);
    }
  }

  function goList() {
    router.navigateByPath("/system/code_list", JSON.parse(decodeURI(router.query("searchParams") || "{}")));
  }

  return (
    <>
      {/* contents */}
      <div className="contents detail">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>공통코드</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={goList}>{state.editMode == "add" ? "취소" : "목록"}</Button>
            { auth.write &&
            <Button variant="contained" onClick={save}>{state.editMode == "add" ? "등록" : "수정"}</Button>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 내용 */}
        <div className="table-wrap">
          <table>
            <colgroup>
              <col style={{ width: "8%" }} />
              <col style={{ width: "17%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "17%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "17%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "17%" }} />
            </colgroup>
            <tbody>
              <tr>
                <th scope="row">대분류코드<em>*</em></th>
                <td>
                  <FormControl sx={{ m: 1, minWidth: "95%" }} size="small" disabled={state.editMode=="update"}>
                    <Select
                      value={info.codeType}
                      onChange={(e) => {setInfo(prev => ({ ...prev, codeType: e.target.value }))}}
                      displayEmpty
                    >
                    <MenuItem value="">선택</MenuItem>
                    {(state.codeTypeCodes as any[]).map((code) => (
                      <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                    ))}
                    </Select>
                  </FormControl>
                </td>
                <th scope="row">코드<em>*</em></th>
                <td>
                  <Input
                    inputProps={{ maxLength: 30 }}
                    value={info.code}
                    onChange={(e) => {setInfo(prev => ({ ...prev, code: e.target.value }))}}
                    onKeyDown={(e) => {kutil.onKeyDownForInput(e, save)}}
                    disabled={state.editMode=="update"}
                  />
                </td>
                <th scope="row">코드명<em>*</em></th>
                <td>
                  <Input
                    inputProps={{ maxLength: 100 }}
                    value={info.value}
                    onChange={(e) => {setInfo(prev => ({ ...prev, value: e.target.value }))}}
                    onKeyDown={(e) => {kutil.onKeyDownForInput(e, save)}}
                  />
                </td>
                <th scope="row">정렬순서<em>*</em></th>
                <td>
                  <Input
                    inputProps={{ maxLength: 3 }}
                    value={info.sortOrder}
                    onChange={(e) => {setInfo(prev => ({ ...prev, sortOrder: valueFormatter.number(valueFormatter.numberParser(e.target.value)) }))}}
                    onKeyDown={(e) => {kutil.onKeyDownForInput(e, save)}}
                  />
                </td>
              </tr>
              <tr>
                <th scope="row">사용여부</th>
                <td>
                  <div className="form-base">
                    <FormControl>
                      <RadioGroup
                        row
                        value={info.useYn}
                        onChange={(e) => {setInfo(prev => ({ ...prev, useYn: e.target.value }))}}
                      >
                      {(commonCode.useYn as any[]).map((code) => (
                        <FormControlLabel key={code.code} value={code.code} control={<Radio size="small" />} label={code.value} />
                      ))}
                      </RadioGroup>
                    </FormControl>
                  </div>
                </td>
                <td colSpan={7}></td>
              </tr>
            </tbody>
          </table>
        </div>
        {/*// 내용 */}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(CodeDetail);
